import {
  TInputImage,
  TSideBySidePair,
  TSideBySideProcessedImage,
  TPostSideBySideUnsortedBatchResponse,
} from "../../../../../services/api-clinic";

export type MockScenarioKey =
  | "paired-balanced"
  | "unpaired-only"
  | "mixed-pairs";

export type MockScenarioOption = {
  key: MockScenarioKey;
  label: string;
  description: string;
};

type TMetadataImageDetails = {
  id_in_database: number;
  preprocessed_image: {
    ideal_crop_coords: number[] | null;
    dimensions: number[];
  };
};

type TMetadataPair = {
  before: TInputImage;
  after: TInputImage;
  concat?: {
    url: string;
    s3_key: string;
    thumbnail_s3_key: string;
    filename: string;
    id_in_database: number;
  };
};

type TMockMetadataResponse = {
  success: boolean;
  all_pairs: TMetadataPair[];
  predefined_pairs: TMetadataPair[];
  high_confidence_pairs: TMetadataPair[];
  low_confidence_pairs: TMetadataPair[];
  paired_with_orientation: unknown[];
  unpaired_before_images: TInputImage[];
  unpaired_after_images: TInputImage[];
  errors: string | null;
  image_details_before: TMetadataImageDetails[];
  image_details_after: TMetadataImageDetails[];
};

type TScenarioDefinition = {
  label: string;
  description: string;
  metadata: TMockMetadataResponse;
};

const MOCK_CDN_BASE_URL = "https://clinic-os.com/try-our-ai-assets";

const imageUrl = (index: number) => `${MOCK_CDN_BASE_URL}/model-${index}.png`;

const toInputImage = (id: number, label: "before" | "after"): TInputImage => {
  const imageIndex = ((id - 1) % 10) + 1;
  return {
    url: imageUrl(imageIndex),
    s3_key: `mock/${label}/model-${imageIndex}.png`,
    processed_s3_key: `mock/${label}/model-${imageIndex}-processed.png`,
    processed_get_url: imageUrl(imageIndex),
    label,
    thumbnail_s3_key: `mock/${label}/model-${imageIndex}-thumb.jpg`,
    id_in_database: id,
  };
};

const toDetails = (
  id: number,
  dims: [number, number] = [1200, 1600],
  crop: [number, number, number, number] | null = [100, 140, 1100, 1460],
): TMetadataImageDetails => ({
  id_in_database: id,
  preprocessed_image: {
    ideal_crop_coords: crop,
    dimensions: dims,
  },
});

const buildPairs = (
  befores: TInputImage[],
  afters: TInputImage[],
): TMetadataPair[] =>
  befores.slice(0, Math.min(befores.length, afters.length)).map((before, i) => {
    const after = afters[i];
    return {
      before,
      after,
      concat: {
        url: imageUrl(((i + 7) % 10) + 1),
        s3_key: `mock/concat/${i + 1}.png`,
        thumbnail_s3_key: `mock/concat/${i + 1}-thumb.jpg`,
        filename: `mock-concat-${i + 1}.png`,
        id_in_database: 5000 + i + 1,
      },
    };
  });

const BEFORE_IMAGES = [9012, 9013, 9014, 9015, 9016, 9017].map((id) =>
  toInputImage(id, "before"),
);
const AFTER_IMAGES = [9018, 9019, 9020, 9021, 9022, 9023].map((id) =>
  toInputImage(id, "after"),
);

const BEFORE_DETAILS: TMetadataImageDetails[] = [
  toDetails(9012),
  toDetails(9013),
  toDetails(9014),
  toDetails(9015),
  toDetails(9016),
  toDetails(9017),
];

const AFTER_DETAILS: TMetadataImageDetails[] = [
  toDetails(9018, [382, 510], null),
  toDetails(9019, [382, 510], null),
  toDetails(9020),
  toDetails(9021),
  toDetails(9022),
  toDetails(9023),
];

const SCENARIOS: Record<MockScenarioKey, TScenarioDefinition> = {
  "paired-balanced": {
    label: "Paired (balanced)",
    description: "All images return in all_pairs. Fast happy path.",
    metadata: {
      success: true,
      all_pairs: buildPairs(
        BEFORE_IMAGES.slice(0, 5),
        AFTER_IMAGES.slice(0, 5),
      ),
      predefined_pairs: [],
      high_confidence_pairs: [],
      low_confidence_pairs: [],
      paired_with_orientation: [],
      unpaired_before_images: [],
      unpaired_after_images: [],
      errors: null,
      image_details_before: BEFORE_DETAILS,
      image_details_after: AFTER_DETAILS,
    },
  },
  "unpaired-only": {
    label: "Unpaired only",
    description:
      "Mimics your payload: empty all_pairs with only unpaired before/after arrays.",
    metadata: {
      success: true,
      all_pairs: [],
      predefined_pairs: [],
      high_confidence_pairs: [],
      low_confidence_pairs: [],
      paired_with_orientation: [],
      unpaired_before_images: BEFORE_IMAGES.slice(0, 5),
      unpaired_after_images: AFTER_IMAGES.slice(0, 5),
      errors: null,
      image_details_before: BEFORE_DETAILS,
      image_details_after: AFTER_DETAILS,
    },
  },
  "mixed-pairs": {
    label: "Mixed pairs + unpaired",
    description: "Two explicit pairs and the rest left unpaired.",
    metadata: {
      success: true,
      all_pairs: buildPairs(
        BEFORE_IMAGES.slice(0, 2),
        AFTER_IMAGES.slice(0, 2),
      ),
      predefined_pairs: [],
      high_confidence_pairs: [],
      low_confidence_pairs: [],
      paired_with_orientation: [],
      unpaired_before_images: BEFORE_IMAGES.slice(2, 5),
      unpaired_after_images: AFTER_IMAGES.slice(2, 5),
      errors: null,
      image_details_before: BEFORE_DETAILS,
      image_details_after: AFTER_DETAILS,
    },
  },
};

export const getMockScenarioOptions = (): MockScenarioOption[] =>
  (Object.keys(SCENARIOS) as MockScenarioKey[]).map((key) => ({
    key,
    label: SCENARIOS[key].label,
    description: SCENARIOS[key].description,
  }));

const MOCK_AUTO_FIT_PADDING_PX = 20;
const dimensionPromiseCache = new Map<string, Promise<[number, number]>>();

const loadImageDimensions = async (url: string): Promise<[number, number]> => {
  if (dimensionPromiseCache.has(url)) {
    return dimensionPromiseCache.get(url)!;
  }

  const promise = new Promise<[number, number]>((resolve) => {
    const img = new Image();
    img.onload = () => {
      const width = Math.max(1, img.naturalWidth || img.width || 1);
      const height = Math.max(1, img.naturalHeight || img.height || 1);
      resolve([width, height]);
    };
    img.onerror = () => {
      resolve([1200, 1600]);
    };
    img.src = url;
  });

  dimensionPromiseCache.set(url, promise);
  return promise;
};

const toCropCoords = (
  dimensions: number[],
): {
  cropX: number;
  cropY: number;
  cropWidth: number;
  cropHeight: number;
} => {
  const width = Math.max(1, dimensions[0] ?? 1);
  const height = Math.max(1, dimensions[1] ?? 1);
  const safePaddingX = Math.min(
    MOCK_AUTO_FIT_PADDING_PX,
    Math.floor(width / 2),
  );
  const safePaddingY = Math.min(
    MOCK_AUTO_FIT_PADDING_PX,
    Math.floor(height / 2),
  );
  const cropWidth = Math.max(1, width - safePaddingX * 2);
  const cropHeight = Math.max(1, height - safePaddingY * 2);

  return {
    cropX: safePaddingX,
    cropY: safePaddingY,
    cropWidth,
    cropHeight,
  };
};

const toMockCensorship = (dimensions: [number, number]) => {
  const width = Math.max(1, dimensions[0]);
  const height = Math.max(1, dimensions[1]);
  const boxWidth = Math.max(24, Math.round(width * 0.2));
  const boxHeight = Math.max(24, Math.round(height * 0.12));
  const centerX = Math.round(width * 0.5);
  const centerY = Math.round(height * 0.56);

  return {
    finalBboxesOnOriginal: {
      private_primary: [
        Math.max(0, centerX - Math.round(boxWidth / 2)),
        Math.max(0, centerY - Math.round(boxHeight / 2)),
        Math.min(width, centerX + Math.round(boxWidth / 2)),
        Math.min(height, centerY + Math.round(boxHeight / 2)),
      ] as [number, number, number, number],
    },
  };
};

const toResponsePair = (
  pair: TMetadataPair,
  index: number,
  beforeDimensionsMap: Map<number, [number, number]>,
  afterDimensionsMap: Map<number, [number, number]>,
): TSideBySidePair => {
  const beforeDimensions = beforeDimensionsMap.get(
    pair.before.id_in_database,
  ) ?? [1200, 1600];
  const afterDimensions = afterDimensionsMap.get(pair.after.id_in_database) ?? [
    1200, 1600,
  ];

  return {
    before: {
      ...pair.before,
      processed_get_url: pair.before.processed_get_url || pair.before.url,
      imageDimensions: {
        width: beforeDimensions[0],
        height: beforeDimensions[1],
      },
      cropCoords: toCropCoords(beforeDimensions),
      censorship: toMockCensorship(beforeDimensions),
    },
    after: {
      ...pair.after,
      processed_get_url: pair.after.processed_get_url || pair.after.url,
      imageDimensions: {
        width: afterDimensions[0],
        height: afterDimensions[1],
      },
      cropCoords: toCropCoords(afterDimensions),
      censorship: toMockCensorship(afterDimensions),
    },
    concat: {
      put_url: pair.concat?.url ?? imageUrl(((index + 7) % 10) + 1),
      s3_key: pair.concat?.s3_key ?? `mock/concat/${index + 1}.png`,
      thumbnail_s3_key:
        pair.concat?.thumbnail_s3_key ?? `mock/concat/${index + 1}-thumb.jpg`,
      filename: pair.concat?.filename ?? `mock-concat-${index + 1}.png`,
      id_in_database: pair.concat?.id_in_database ?? 8000 + index,
    },
  };
};

export const getMockPayloadForScenario = async (key: MockScenarioKey) => {
  const scenario = SCENARIOS[key];
  const metadata = scenario.metadata;

  const pairsFromMetadata = metadata.all_pairs;
  const responsePairs = pairsFromMetadata;

  const allBeforeInputImages = [
    ...metadata.unpaired_before_images,
    ...responsePairs.map((pair) => pair.before),
  ];
  const allAfterInputImages = [
    ...metadata.unpaired_after_images,
    ...responsePairs.map((pair) => pair.after),
  ];

  const uniqueBeforeImages = Array.from(
    new Map(
      allBeforeInputImages.map((image) => [image.id_in_database, image]),
    ).values(),
  );
  const uniqueAfterImages = Array.from(
    new Map(
      allAfterInputImages.map((image) => [image.id_in_database, image]),
    ).values(),
  );

  const beforeDimensionsEntries = await Promise.all(
    uniqueBeforeImages.map(async (image) => {
      const dims = await loadImageDimensions(
        image.processed_get_url || image.url,
      );
      return [image.id_in_database, dims] as const;
    }),
  );
  const afterDimensionsEntries = await Promise.all(
    uniqueAfterImages.map(async (image) => {
      const dims = await loadImageDimensions(
        image.processed_get_url || image.url,
      );
      return [image.id_in_database, dims] as const;
    }),
  );

  const beforeDimensionsMap = new Map<number, [number, number]>(
    beforeDimensionsEntries,
  );
  const afterDimensionsMap = new Map<number, [number, number]>(
    afterDimensionsEntries,
  );

  const enrichedMetadata = {
    ...metadata,
    image_details_before: metadata.image_details_before.map((detail) => ({
      ...detail,
      preprocessed_image: {
        ...detail.preprocessed_image,
        dimensions:
          beforeDimensionsMap.get(detail.id_in_database) ??
          detail.preprocessed_image.dimensions,
      },
    })),
    image_details_after: metadata.image_details_after.map((detail) => ({
      ...detail,
      preprocessed_image: {
        ...detail.preprocessed_image,
        dimensions:
          afterDimensionsMap.get(detail.id_in_database) ??
          detail.preprocessed_image.dimensions,
      },
    })),
  };

  const allPairs = responsePairs.map((pair, index) =>
    toResponsePair(pair, index, beforeDimensionsMap, afterDimensionsMap),
  );

  const toUnpairedProcessed = (
    image: TInputImage,
    dimensionsMap: Map<number, [number, number]>,
  ): TSideBySideProcessedImage => {
    const dimensions = dimensionsMap.get(image.id_in_database) ?? [1200, 1600];
    return {
      ...image,
      processed_get_url: image.processed_get_url || image.url,
      imageDimensions: {
        width: dimensions[0],
        height: dimensions[1],
      },
      cropCoords: toCropCoords(dimensions),
      censorship: toMockCensorship(dimensions),
    };
  };

  const unpairedBeforeImages = metadata.unpaired_before_images.map((image) =>
    toUnpairedProcessed(image, beforeDimensionsMap),
  );
  const unpairedAfterImages = metadata.unpaired_after_images.map((image) =>
    toUnpairedProcessed(image, afterDimensionsMap),
  );

  const beforeAfterResponse: TPostSideBySideUnsortedBatchResponse = {
    allPairs,
    unpairedBeforeImages,
    unpairedAfterImages,
  };

  return {
    metadata: enrichedMetadata,
    beforeAfterResponse,
    processedImageUrls: {
      preOp: enrichedMetadata.image_details_before
        .map((detail) =>
          BEFORE_IMAGES.find(
            (image) => image.id_in_database === detail.id_in_database,
          ),
        )
        .filter((img): img is TInputImage => !!img)
        .map((img) => img.processed_get_url || img.url),
      postOp: enrichedMetadata.image_details_after
        .map((detail) =>
          AFTER_IMAGES.find(
            (image) => image.id_in_database === detail.id_in_database,
          ),
        )
        .filter((img): img is TInputImage => !!img)
        .map((img) => img.processed_get_url || img.url),
    },
  };
};
